import React, { useEffect, useState } from "react";
import { ShieldCheck, Save, UserPlus, History, Pencil, Trash2, ChevronDown, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Spinner, PageHeader, Empty } from "@/components/shared";
import { FieldW, SelectBox } from "@/pages/Clients";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function Settings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(false);
  const [userDlg, setUserDlg] = useState(false);
  const [uEditId, setUEditId] = useState(null);
  const [uForm, setUForm] = useState({ email: "", password: "", name: "", role: "kitchen", client_id: "", active: true });
  const [aFilter, setAFilter] = useState({ entity: "", action: "", q: "" });
  const [openAudit, setOpenAudit] = useState(null);
  const [resetDlg, setResetDlg] = useState(false);
  const [resetPhrase, setResetPhrase] = useState("");

  const doReset = async () => {
    setBusy(true);
    try {
      await api.post("/admin/reset-to-live", { confirm: resetPhrase });
      toast.success("System reset to live — all demo/operational data removed");
      setResetDlg(false); setResetPhrase("");
      setTimeout(() => window.location.reload(), 900);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const loadAudit = () => {
    const params = {};
    if (aFilter.entity) params.entity = aFilter.entity;
    if (aFilter.action) params.action = aFilter.action;
    if (aFilter.q) params.q = aFilter.q;
    api.get("/audit", { params }).then((r) => setAudit(r.data)).catch(() => {});
  };

  const load = () => {
    api.get("/settings").then((r) => setSettings(r.data));
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/audit").then((r) => setAudit(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
  const saveSettings = async () => {
    setBusy(true);
    try {
      await api.put("/settings", {
        default_markup_pct: Number(settings.default_markup_pct),
        expiring_soon_days: Number(settings.expiring_soon_days),
        kitchen_name: settings.kitchen_name,
        default_op_start: settings.default_op_start, default_op_end: settings.default_op_end,
        inventory_deduction_stage: settings.inventory_deduction_stage,
      });
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const createUser = async () => {
    try {
      if (uEditId) {
        const body = { name: uForm.name, role: uForm.role, client_id: uForm.role === "client" ? uForm.client_id : null, active: uForm.active };
        if (uForm.password) body.password = uForm.password;
        await api.put(`/users/${uEditId}`, body);
        toast.success("User updated");
      } else {
        await api.post("/users", { ...uForm, client_id: uForm.role === "client" ? uForm.client_id : null });
        toast.success("User created");
      }
      setUserDlg(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openEditUser = (u) => {
    setUForm({ email: u.email, password: "", name: u.name || "", role: u.role, client_id: u.client_id || "", active: u.active !== false });
    setUEditId(u.id); setUserDlg(true);
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!settings) return <Spinner />;

  return (
    <div>
      <PageHeader title="Settings & Audit" subtitle="Kitchen configuration, users and audit trail" />
      <Tabs defaultValue="config">
        <TabsList className="mb-5">
          <TabsTrigger value="config" data-testid="tab-config">Configuration</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit Trail</TabsTrigger>
        </TabsList>

        <TabsContent value="config">
          <div className="card-surface p-6 max-w-xl">
            <div className="grid sm:grid-cols-2 gap-4">
              <FieldW label="Kitchen name" full><Input value={settings.kitchen_name || ""} onChange={(e) => set("kitchen_name", e.target.value)} data-testid="set-kitchen-name" /></FieldW>
              <FieldW label="Default markup %"><Input type="number" data-testid="set-markup" value={settings.default_markup_pct} onChange={(e) => set("default_markup_pct", e.target.value)} /></FieldW>
              <FieldW label="Expiring-soon window (days)"><Input type="number" value={settings.expiring_soon_days} onChange={(e) => set("expiring_soon_days", e.target.value)} /></FieldW>
              <FieldW label="Default operating start"><Input type="time" value={settings.default_op_start || ""} onChange={(e) => set("default_op_start", e.target.value)} /></FieldW>
              <FieldW label="Default operating end"><Input type="time" value={settings.default_op_end || ""} onChange={(e) => set("default_op_end", e.target.value)} /></FieldW>
              <FieldW label="Inventory deduction stage" full>
                <SelectBox value={settings.inventory_deduction_stage || "Accepted"} onChange={(v) => set("inventory_deduction_stage", v)}
                  options={[{ value: "Accepted", label: "On Accepted" }, { value: "In Production", label: "In Production" }, { value: "Completed", label: "On Completed" }]} testid="set-deduction-stage" />
              </FieldW>
            </div>
            <p className="text-xs text-muted-foreground mt-4">Capacity warning thresholds: {(settings.capacity_warning_pcts || []).join("%, ")}%</p>
            <Button onClick={saveSettings} disabled={busy} data-testid="save-settings-btn" className="mt-5 gap-1.5 bg-primary text-primary-foreground hover:bg-amber-400 font-semibold">
              <Save className="h-4 w-4" /> Save Settings
            </Button>
          </div>
          {user?.role === "admin" && (
            <div className="card-surface p-6 max-w-xl mt-6 border-rose-500/40" data-testid="danger-zone">
              <div className="flex items-center gap-2 mb-2"><AlertTriangle className="h-5 w-5 text-rose-400" /><h3 className="font-display font-semibold text-white">Danger Zone</h3></div>
              <p className="text-sm text-muted-foreground mb-4">Permanently remove ALL clients, brands, menus, recipes, ingredients, inventory, purchases, orders, billing, wastage, documents, notifications and activity logs. Your Super Admin account and system settings are preserved. This cannot be undone — take a backup first.</p>
              <Button variant="outline" onClick={() => setResetDlg(true)} data-testid="reset-live-btn" className="gap-1.5 border-rose-500/50 text-rose-400 hover:bg-rose-500/10">
                <Trash2 className="h-4 w-4" /> Reset system to live
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="users">
          <div className="flex justify-end mb-4">
            <Button onClick={() => { setUEditId(null); setUForm({ email: "", password: "", name: "", role: "kitchen", client_id: clients[0]?.id || "", active: true }); setUserDlg(true); }} data-testid="add-user-btn" className="gap-1.5 bg-primary text-primary-foreground hover:bg-amber-400 font-semibold">
              <UserPlus className="h-4 w-4" /> New User
            </Button>
          </div>
          <div className="card-surface overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent border-border"><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Client</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} className="border-border">
                    <TableCell className="text-white font-medium">{u.name}</TableCell>
                    <TableCell className="text-slate-300 font-mono text-sm">{u.email}</TableCell>
                    <TableCell className="capitalize text-amber-300">{u.role}</TableCell>
                    <TableCell className="text-slate-400 text-sm">{clients.find((c) => c.id === u.client_id)?.name || "—"}</TableCell>
                    <TableCell>{u.active === false ? <span className="text-xs text-rose-400">Deactivated</span> : <span className="text-xs text-emerald-400">Active</span>}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => openEditUser(u)} data-testid={`edit-user-${u.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => deleteUser(u)} data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4 text-rose-400" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="audit">
          <div className="flex flex-wrap items-end gap-2 mb-4">
            <div><label className="text-[11px] text-muted-foreground">Entity</label><div className="mt-1 w-40"><SelectBox value={aFilter.entity || "__all__"} onChange={(v) => setAFilter({ ...aFilter, entity: v === "__all__" ? "" : v })} options={[{ value: "__all__", label: "All" }, ...["client", "brand", "ingredient", "menu_item", "order", "purchase", "user", "change_request", "ai_document", "settings"].map((e) => ({ value: e, label: e }))]} testid="audit-entity" /></div></div>
            <div><label className="text-[11px] text-muted-foreground">Action contains</label><Input value={aFilter.action} onChange={(e) => setAFilter({ ...aFilter, action: e.target.value })} className="mt-1 w-40" data-testid="audit-action" /></div>
            <div className="flex-1 min-w-[180px]"><label className="text-[11px] text-muted-foreground">Search (user / id / reason)</label><Input value={aFilter.q} onChange={(e) => setAFilter({ ...aFilter, q: e.target.value })} className="mt-1" data-testid="audit-search" /></div>
            <Button variant="outline" onClick={loadAudit} data-testid="audit-filter-btn">Filter</Button>
          </div>
          {audit.length === 0 ? <Empty icon={History} title="No audit records" /> : (
            <div className="card-surface overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent border-border"><TableHead>Time</TableHead><TableHead>User</TableHead><TableHead>Action</TableHead><TableHead>Entity</TableHead><TableHead>Reason</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {audit.map((a) => (
                    <React.Fragment key={a.id}>
                      <TableRow className="border-border cursor-pointer" onClick={() => setOpenAudit(openAudit === a.id ? null : a.id)} data-testid={`audit-row-${a.id}`}>
                        <TableCell className="font-mono text-xs text-muted-foreground">{new Date(a.at).toLocaleString()}</TableCell>
                        <TableCell className="text-slate-300 text-sm">{a.user_email}</TableCell>
                        <TableCell className="capitalize text-amber-300">{a.action}</TableCell>
                        <TableCell className="text-slate-300">{a.entity}</TableCell>
                        <TableCell className="text-slate-400 text-sm">{a.reason || "—"}</TableCell>
                        <TableCell className="text-right"><ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${openAudit === a.id ? "rotate-180" : ""}`} /></TableCell>
                      </TableRow>
                      {openAudit === a.id && (
                        <TableRow className="border-border bg-secondary/20">
                          <TableCell colSpan={6}>
                            <div className="grid md:grid-cols-2 gap-3 py-1" data-testid={`audit-detail-${a.id}`}>
                              <div>
                                <p className="text-[11px] uppercase tracking-wide text-rose-400 font-semibold mb-1">Previous value</p>
                                <pre className="text-xs text-slate-300 bg-background/60 rounded-lg p-3 overflow-x-auto max-h-56">{a.old_value ? JSON.stringify(a.old_value, null, 2) : "—"}</pre>
                              </div>
                              <div>
                                <p className="text-[11px] uppercase tracking-wide text-emerald-400 font-semibold mb-1">New value</p>
                                <pre className="text-xs text-slate-300 bg-background/60 rounded-lg p-3 overflow-x-auto max-h-56">{a.new_value ? JSON.stringify(a.new_value, null, 2) : "—"}</pre>
                              </div>
                              <p className="md:col-span-2 text-[11px] text-muted-foreground font-mono">{a.entity} · {a.entity_id || "—"} · by {a.user_email} ({a.user_role})</p>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={resetDlg} onOpenChange={setResetDlg}>
        <DialogContent className="max-w-md bg-card border-border" data-testid="reset-dialog">
          <DialogHeader><DialogTitle className="text-rose-400 flex items-center gap-2"><AlertTriangle className="h-5 w-5" /> Reset system to live</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-300">This permanently deletes ALL operational and demo data across every module. Your Super Admin account and settings are kept. Type <b className="font-mono text-white">RESET TO LIVE</b> to confirm.</p>
          <Input value={resetPhrase} onChange={(e) => setResetPhrase(e.target.value)} placeholder="RESET TO LIVE" data-testid="reset-phrase" className="mt-1" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDlg(false)}>Cancel</Button>
            <Button onClick={doReset} disabled={busy || resetPhrase !== "RESET TO LIVE"} data-testid="confirm-reset-btn" className="bg-rose-500 text-white hover:bg-rose-600 font-semibold">Delete everything</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={userDlg} onOpenChange={setUserDlg}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader><DialogTitle>{uEditId ? "Edit User" : "New User"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <FieldW label="Name"><Input value={uForm.name} onChange={(e) => setUForm({ ...uForm, name: e.target.value })} data-testid="user-name" /></FieldW>
            <FieldW label="Email"><Input value={uForm.email} disabled={!!uEditId} onChange={(e) => setUForm({ ...uForm, email: e.target.value })} data-testid="user-email" /></FieldW>
            <FieldW label={uEditId ? "New password (leave blank to keep)" : "Password"}><Input type="password" value={uForm.password} onChange={(e) => setUForm({ ...uForm, password: e.target.value })} data-testid="user-password" /></FieldW>
            <FieldW label="Role"><SelectBox value={uForm.role} onChange={(v) => setUForm({ ...uForm, role: v })} options={[{ value: "admin", label: "Super Admin" }, { value: "kitchen", label: "Kitchen / Operations" }, { value: "finance", label: "Finance" }, { value: "client", label: "Client" }]} testid="user-role" /></FieldW>
            {uForm.role === "client" && <FieldW label="Client"><SelectBox value={uForm.client_id} onChange={(v) => setUForm({ ...uForm, client_id: v })} options={clients.map((c) => ({ value: c.id, label: c.name }))} /></FieldW>}
            {uEditId && (
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <div><span className="text-sm text-white">Account active</span><p className="text-xs text-muted-foreground">Deactivated users cannot log in</p></div>
                <Switch checked={uForm.active} onCheckedChange={(v) => setUForm({ ...uForm, active: v })} data-testid="user-active" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserDlg(false)}>Cancel</Button>
            <Button onClick={createUser} disabled={!uForm.email || (!uEditId && !uForm.password)} data-testid="save-user-btn" className="bg-primary text-primary-foreground hover:bg-amber-400 font-semibold">{uEditId ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
